import { z } from 'zod/v4';
import type { ResolvedChecklist } from './planning/blueprint/types';

/**
 * Matches the couple's words against checklist task names, best match first. Runs in the
 * browser, which holds the resolved checklist with names and done state, so the server
 * stores no name map.
 */

// Most matches one lookup returns. A named task is one row, not a list.
export const DEFAULT_TASK_MATCHES = 10;
export const MAX_TASK_MATCHES = 25;

// Words that mean nothing for a match. Short words drop out by length.
const STOP_WORDS = new Set(['the', 'and', 'for', 'your', 'with', 'our', 'you', 'that', 'this']);

export type ChecklistTaskRow = {
  id: string;
  name: string;
  done: boolean;
  order: number;
  group?: string;
  groupName?: string;
  custom?: boolean;
};

export const checklistLookupInputSchema = z.object({
  query: z
    .string()
    .min(2)
    .describe(
      "Words from the task's or its group's name, as the couple said them, e.g. 'book florist'.",
    ),
  limit: z.number().int().min(1).max(MAX_TASK_MATCHES).default(DEFAULT_TASK_MATCHES),
  includeDone: z
    .boolean()
    .default(false)
    .describe('Also return tasks already done, for an untick.'),
});

export const checklistLookupOutputSchema = z.object({
  total: z.number(),
  matched: z.number(),
  tasks: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      done: z.boolean(),
      custom: z.boolean(),
      groupId: z.string(),
      groupName: z.string(),
    }),
  ),
  note: z.string().optional(),
});

export type ChecklistLookupOutput = z.infer<typeof checklistLookupOutputSchema>;

// The words a match counts: lower case, three letters or more.
const words = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 3 && !STOP_WORDS.has(word));

/**
 * Tasks whose name has the query's words, best first: one point per word found. Ties keep the
 * checklist order. Open tasks only unless `includeDone`, as the lookup finds a task to tick.
 */
export const matchTasks = (
  tasks: readonly ChecklistTaskRow[],
  query: string,
  { limit = DEFAULT_TASK_MATCHES, includeDone = false } = {},
): ChecklistTaskRow[] => {
  const needles = words(query);
  if (needles.length === 0) return [];
  return tasks
    .filter((task) => includeDone || !task.done)
    .map((task) => {
      const haystack = `${task.name} ${task.groupName ?? ''}`.toLowerCase();
      const score = needles.filter((needle) => haystack.includes(needle)).length;
      return { task, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.task.order - b.task.order)
    .slice(0, limit)
    .map(({ task }) => task);
};

const empty = (note: string): ChecklistLookupOutput => ({ total: 0, matched: 0, tasks: [], note });

/**
 * The lookup result for one call, from the checklist in the browser. Returns nothing while
 * the checklist loads, or for a projected checklist, whose tasks are not the couple's own.
 */
export const lookupChecklistTasks = (
  checklist: ResolvedChecklist | null,
  input: unknown,
): ChecklistLookupOutput => {
  const parsed = checklistLookupInputSchema.safeParse(input);
  if (!parsed.success) {
    return empty('The query must be a few words from the task name.');
  }
  if (!checklist) {
    return empty('Their checklist is still loading in the app. Try again in a moment.');
  }
  if (checklist.projected) {
    return empty('They have no checklist yet, so there is no task to tick.');
  }
  const { query, limit, includeDone } = parsed.data;
  const matches = matchTasks(checklist.tasks, query, { limit, includeDone });
  return {
    total: checklist.tasks.length,
    matched: matches.length,
    tasks: matches.map(({ id, name, done, custom, group, groupName }) => ({
      id,
      name,
      done,
      custom: Boolean(custom),
      groupId: group ?? '',
      groupName: groupName ?? '',
    })),
    ...(matches.length === 0
      ? { note: 'No task carries those words. Try fewer or different words from its name.' }
      : {}),
  };
};
